
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import { environment } from 'src/environments/environment';
import { CurriculumItem, CurriculumItemResource, CurriculumItemStatistic, TreeItemsGroup, ScopeAndSequence } from '../../_models/index';
import { LmsService } from './lms.service';
import { tap, map } from 'rxjs/operators';


@Injectable({
    providedIn: 'root'
})
export class CurriculumItemService {


    private apiUrl = `${environment.apiUrl}/CurriculumItem`;
    constructor(private http: HttpClient) {
    }

    getById(curriculumItemId: number): Observable<CurriculumItem> {
        return this.http.get<CurriculumItem>(`${this.apiUrl}/GetById/${curriculumItemId}`);
    }

    getByIdWithObjectivesWithParents(curriculumItemId: number): Observable<CurriculumItem> {
        return this.http.get<CurriculumItem>(`${this.apiUrl}/GetByIdWithObjectivesWithParents/${curriculumItemId}`);
    }

    getTreeItemsGroup(subjectId: number, sectionSubjectId: number, parentId: number): Observable<TreeItemsGroup> {
        const query = `?sectionSubjectId=${sectionSubjectId ? sectionSubjectId : ''}&parentId=${parentId ? parentId : ''}`;
        return this.http.get<TreeItemsGroup>(`${this.apiUrl}/GetTreeItemsGroup/${subjectId}${query}`);
    }

    ////////////////////////////////////////

    getTopItemsWithChildrenForObjective(objectiveId: number): Observable<CurriculumItem[]> {
        return this.http.get<CurriculumItem[]>(`${this.apiUrl}/GetTopItemsWithChildrenForObjective/${objectiveId}`);
    }

    getTopItemsBySectionSubjectIdWithChildrenWitResourcesOnLessons(sectionSubjectId: number): Observable<CurriculumItem[]> {
        return this.http.get<CurriculumItem[]>(
            `${this.apiUrl}/GetTopItemsBySectionSubjectIdWithChildrenWitResourcesOnLessons/${sectionSubjectId}`);
    }

    getTopItemsWithChildrenBySubjectIdAndSectionSubjectIdWithResourcesAndObjectives(subjectId: number, sectionSubjectId: number): Observable<CurriculumItem[]> {
        return this.http.get<CurriculumItem[]>(
            `${this.apiUrl}/GetTopItemsWithChildrenBySubjectIdAndSectionSubjectIdWithResourcesAndObjectives/${subjectId}${sectionSubjectId ? '/' + sectionSubjectId : ''}`);
    }

    getCurriculumItemStatistic(curriculumItemId: number): Observable<CurriculumItemStatistic> {
        return this.http.get<CurriculumItemStatistic>(`${this.apiUrl}/GetCurriculumItemStatistic/${curriculumItemId}`);
    }

    ////////////////////////////////////////

    addCurriculumItem(curriculumItem: CurriculumItem, objectivesChanged = false): Observable<CurriculumItem> {
        return this.http.post<CurriculumItem>(`${this.apiUrl}/AddCurriculumItem`, curriculumItem)
            .pipe(tap(item => {
                LmsService.isCurriculumHasCurriculumItems = true;
                LmsService.curriculumItemChanged$.next();
                if (objectivesChanged)
                    LmsService.objectivesChanged$.next();
                return item;
            }));
    }

    updateCurriculumItem(curriculumItem: CurriculumItem, objectivesChanged = false): Observable<CurriculumItem> {
        return this.http.put<CurriculumItem>(`${this.apiUrl}/UpdateCurriculumItem`, curriculumItem)
            .pipe(tap(c => {
                LmsService.curriculumItemChanged$.next();
                LmsService.curriculumItemUpdated$.next();
                if (objectivesChanged)
                    LmsService.objectivesChanged$.next();
                return c;
            }));
    }

    updateCurriculumItemIsLive(curriculumItemId: number, isLive: boolean): Observable<CurriculumItem> {
        return this.http.put<CurriculumItem>(`${this.apiUrl}/UpdateCurriculumItemIsLive/${curriculumItemId}/${isLive}`, {});
    }

    deleteCurriculumItem(curriculumItemId: number): Observable<CurriculumItem> {
        return this.http.delete<CurriculumItem>(`${this.apiUrl}/DeleteCurriculumItem/${curriculumItemId}`)
            .pipe(tap(item => {
                this.checkIsSubjectHasCurriculumItems(item.subjectId);
                LmsService.curriculumItemChanged$.next();
                LmsService.curriculumItemDeleted$.next();
                return item;
            }));
    }

    ////////////////////////////////////////

    getCurriculumItemResources(curriculumItemId: number): Observable<CurriculumItemResource[]> {
        return this.http.get<CurriculumItemResource[]>(`${this.apiUrl}/GetCurriculumItemResources/${curriculumItemId}`);
    }

    addCurriculumItemResource(curriculumItemResource: CurriculumItemResource, uploadFile): Observable<CurriculumItemResource> {
        const formData: FormData = new FormData();
        formData.append('curriculumItemResourceId',
            curriculumItemResource.curriculumItemResourceId + '' ? curriculumItemResource.curriculumItemResourceId + '' : '');
        formData.append('curriculumItemId',
            curriculumItemResource.curriculumItemId ? curriculumItemResource.curriculumItemId + '' : '');
        formData.append('type',
            curriculumItemResource.type ? curriculumItemResource.type + '' : '');
        formData.append('title', curriculumItemResource.title ? curriculumItemResource.title : '');
        formData.append('embedCode', curriculumItemResource.embedCode ? curriculumItemResource.embedCode : '');
        formData.append('fileName', curriculumItemResource.fileName ? curriculumItemResource.fileName : '');
        formData.append('description', curriculumItemResource.description ? curriculumItemResource.description : '');
        formData.append('url', curriculumItemResource.url ? curriculumItemResource.url : '');
        formData.append('key', curriculumItemResource.key ? curriculumItemResource.key : '');
        formData.append('secret', curriculumItemResource.secret ? curriculumItemResource.secret : '');
        if (uploadFile)
            formData.append('file', uploadFile);

        return this.http.post<CurriculumItemResource>(`${this.apiUrl}/AddCurriculumItemResource`, formData)
            .pipe(tap(item => {
                LmsService.curriculumItemResourceChanged$.next();
                return item;
            }));
    }

    updateCurriculumItemResource(curriculumItemResource: CurriculumItemResource, uploadFile): Observable<CurriculumItemResource> {
        const formData: FormData = new FormData();
        formData.append('curriculumItemResourceId',
            curriculumItemResource.curriculumItemResourceId + '' ? curriculumItemResource.curriculumItemResourceId + '' : '');
        formData.append('curriculumItemId',
            curriculumItemResource.curriculumItemId ? curriculumItemResource.curriculumItemId + '' : '');
        formData.append('type',
            curriculumItemResource.type ? curriculumItemResource.type + '' : '');
        formData.append('title', curriculumItemResource.title ? curriculumItemResource.title : '');
        formData.append('embedCode', curriculumItemResource.embedCode ? curriculumItemResource.embedCode : '');
        formData.append('fileName', curriculumItemResource.fileName ? curriculumItemResource.fileName : '');
        formData.append('description', curriculumItemResource.description ? curriculumItemResource.description : '');
        formData.append('url', curriculumItemResource.url ? curriculumItemResource.url : '');
        formData.append('key', curriculumItemResource.key ? curriculumItemResource.key : '');
        formData.append('secret', curriculumItemResource.secret ? curriculumItemResource.secret : '');
        if (uploadFile)
            formData.append('file', uploadFile);
        return this.http.put<CurriculumItemResource>(`${this.apiUrl}/UpdateCurriculumItemResource`, formData)
            .pipe(tap(item => {
                LmsService.curriculumItemResourceChanged$.next();
                LmsService.curriculumItemResourceUpdated$.next();
                return item;
            }));
    }

    deleteCurriculumItemResource(curriculumItemResourceId: number): Observable<CurriculumItemResource> {
        return this.http.delete<CurriculumItemResource>(`${this.apiUrl}/DeleteCurriculumItemResource/${curriculumItemResourceId}`)
            .pipe(tap(item => {
                LmsService.curriculumItemResourceChanged$.next();
                LmsService.curriculumItemResourceDeleted$.next();
                return item;
            }));
    }

    ////////////////////////////////////////

    getScopeAndSequence(sectionSubjectId: number): Observable<ScopeAndSequence> {
        return this.http.get<ScopeAndSequence>(`${this.apiUrl}/GetScopeAndSequence/${sectionSubjectId}`);
    }

    setCurriculumItemCompleted(curriculumItemId: number, sectionSubjectId: number): Observable<ScopeAndSequence> {
        return this.http.put<ScopeAndSequence>(`${this.apiUrl}/SetCurriculumItemCompleted/${curriculumItemId}/${sectionSubjectId}`, {});
    }

    setAllChildrenCurriculumItemsCompleted(topCurriculumItemId: number, sectionSubjectId: number): Observable<ScopeAndSequence> {
        return this.http.put<ScopeAndSequence>(`${this.apiUrl}/SetAllChildrenCurriculumItemsCompleted/${topCurriculumItemId}/${sectionSubjectId}`, {});
    }
    ////////////////////////////////////////

    getCurriculumItemResourceFileUrl(fileName: string): Observable<string> {
        return this.http.get<any>(`${environment.apiUrl}/File/GetUrl/${fileName}`)
            .pipe(map(x => {
                return x.url;
            }));
    }

    downloadCurriculumItemResourceFile(fileName: string): Observable<Blob> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/octet-stream',
            'Accept': 'application/octet-stream',
        });
        return this.http.get(`${environment.apiUrl}/File/Download/${fileName}`, { headers: headers, responseType: 'blob' });
    }

    ////////////////////////////////////////
    checkIsSubjectHasCurriculumItems(subjectId: number) {
        this.http.get<boolean>(`${this.apiUrl}/GetIsSubjectHasCurriculumItems/${subjectId}`)
            .subscribe(val => {
                LmsService.isCurriculumHasCurriculumItems = val;
            });
    }

}
